import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

export interface NicheRun {
  runId: string
  workspaceId: string
  weekBucket: string
  selectedCount: number
  rejectedCount: number
  gtmPackCount: number
  recordCount: number
  syncStatus: 'success' | 'failed' | 'running'
  lastSyncTimestamp: string
  selectedNiches: Array<Record<string, unknown>>
  gtmPacks: Array<Record<string, unknown>>
  createdAt: string
}

export interface NicheFreshness {
  workspaceId: string
  lastRunId: string | null
  lastSyncTimestamp: string | null
  recordCount: number
  syncStatus: 'success' | 'failed' | 'running'
  lastError: string | null
  updatedAt: string
}

export interface PipelineParams {
  workspaceId?: string
  seedDomains?: string[]
  geoTargets?: string[]
  minMarketSize?: number
  launchLimit?: number
  toneProfile?: 'direct' | 'consultative' | 'authority'
}

interface NicheState {
  latestRun: NicheRun | null
  history: NicheRun[]
  freshness: NicheFreshness | null
  isRunning: boolean
  isLoading: boolean
  error: string | null

  runPipeline: (params: PipelineParams) => Promise<void>
  fetchLatest: (workspaceId?: string) => Promise<void>
  fetchHistory: (limit?: number) => Promise<void>
  fetchFreshness: (workspaceId?: string) => Promise<void>
}

function baseUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw || '/api'
}

export const useNicheStore = create<NicheState>((set) => ({
  latestRun: null,
  history: [],
  freshness: null,
  isRunning: false,
  isLoading: false,
  error: null,

  runPipeline: async (params) => {
    set({ isRunning: true, error: null })
    try {
      const res = await gatewayFetch(`${baseUrl()}/niche/discovery/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      })
      if (!res.ok) throw new Error(`Pipeline failed: ${res.status}`)
      const data = await res.json()
      set({ latestRun: data, isRunning: false })
    } catch (e) {
      set({ error: (e as Error).message, isRunning: false })
    }
  },

  fetchLatest: async (workspaceId?: string) => {
    set({ isLoading: true, error: null })
    try {
      const params = workspaceId ? `?workspaceId=${workspaceId}` : ''
      const res = await gatewayFetch(`${baseUrl()}/niche/discovery/latest${params}`)
      if (!res.ok) throw new Error(`Failed to fetch latest: ${res.status}`)
      const data = await res.json()
      set({ latestRun: data.latest || null, isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  fetchHistory: async (limit = 20) => {
    set({ isLoading: true, error: null })
    try {
      const res = await gatewayFetch(`${baseUrl()}/niche/discovery/history?limit=${limit}`)
      if (!res.ok) throw new Error(`Failed to fetch history: ${res.status}`)
      const data = await res.json()
      set({ history: data.history || [], isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  fetchFreshness: async (workspaceId?: string) => {
    try {
      const params = workspaceId ? `?workspaceId=${workspaceId}` : ''
      const res = await gatewayFetch(`${baseUrl()}/niche/discovery/freshness${params}`)
      if (!res.ok) return
      const data = await res.json()
      set({ freshness: data.freshness || data })
    } catch {
      // non-critical
    }
  },
}))
